import { Card } from 'antd';
import React from 'react';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import { imageTypes } from '../../../../../const/imageTypes';
import getInitialImageCategoriesValues from '../../../../../lib/getInitialImageCategoriesValues';
import { validationSchema } from '../../../forms/FormEditImage/FormEditImage.const';
import isNotReleased from '../../../../../lib/isNotReleased';
import { imageStatuses } from '../../../../../const/imageStatuses';



const rareFormatIndices = [ 4, 8 ];

const tapsLimitMapping = {
  '<700': [ 1, 5, 9, 12, 13 ],
  '700-999': [ 2, 6, 10, 14 ],
  '1000+': [ 3, 7, 11 ],
  '580-699': [ 15 ],
};

export const initialStorageHeight = window.innerHeight - (window.innerWidth > 1997 ? 2000 : 1400);

export const canUpdatePublicationTime = (previewImage, isEditing) => {
  return isNotReleased(previewImage.release_date)
    && previewImage.status !== imageStatuses.ready_for_release
    && !isEditing;
};

export const shouldSkipGettingImagesTags = (previewImage, planningDetailsImages, isImageAddingToBoard, queriedImageIds) => {
  return (isEmpty(previewImage) && isEmpty(planningDetailsImages) && !isImageAddingToBoard) || isEmpty(queriedImageIds);
};

export const shouldSkipGettingSimilarImages = (isOpenedTagsTab, previewImage, date) => {
  return !isOpenedTagsTab || !previewImage || !previewImage.id || !date;
};

export const getImageTagsQueryParams = (previewImage, queriedImageIds) => {
  let imageTagsQuery = `image_ids=${queriedImageIds.join(',')}`;

  if (!isEmpty(previewImage)) {
    imageTagsQuery += '&sync_image=1';
  }

  return !isEmpty(queriedImageIds) ? imageTagsQuery : null;
};

export const getTotalItemsInStorage = (itemsInStorage, storageTotalCount, formErrors) => {
  const getTotalCount = (itemsInStorage.length > storageTotalCount) ? itemsInStorage.length : storageTotalCount;

  return isEmpty(formErrors) ? getTotalCount : 0;
};

export const getTapsLimit = (index) => Object.keys(tapsLimitMapping).find((key) => tapsLimitMapping[key].includes(index)) || '';

export const getCardFormat = (index) => rareFormatIndices.includes(index)
  ? `Rare (${capitalize(imageTypes.cargo)})` : '';

export const getDefaultEmptyCards = (
  hasActionButton,
  startIndex,
  totalImages,
  isEditing = false,
  generateEmptySlots = false,
) => {
  const emptyCards = [];
  const totalCards = Math.max(15, totalImages + 1);

  // eslint-disable-next-line fp/no-loops
  for (let i = startIndex; i < totalCards; i++) {
    const index = generateEmptySlots ? i + 1 : i;
    const tapsLimit = getTapsLimit(index);
    const format = getCardFormat(index);

    emptyCards.push(
      <Card
        key={`default-${index}`}
        style={{ display: generateEmptySlots && startIndex % 2 === 0 && index > 15 ? 'none' : 'flex' }}
        className={`content-card content-image content-image--empty 
          ${isEditing && hasActionButton ? 'content-image--empty__action' : ''}`}
      >
        <span className="content-image__serial-number">
          <strong>{index}</strong>
        </span>
        <span className="content-image__help-label">
          {tapsLimit}
          {format}
        </span>
      </Card>,
    );
  }

  return emptyCards;
};


export const validateSelectedItems = async ({
  planningBoardImages,
  noLinesHashtagId,
  mysteryCategoryId,
  updateImageWithErrorsIds,
  updateInitialImagesErrors,
  allImageTagsData = [],
}) => {
  const errors = {};

  const imageWithInvalidTagsIds = [];

  const validationPromises = planningBoardImages.map(async (item) => {
    const tagFromAllImageTags = allImageTagsData.find((tag) => tag.image_id === item.id);

    // always set true by default because validation triggers several times when 'isTagsValid' param may not exist yet
    const isTagsValid = item?.isTagsValid ?? tagFromAllImageTags?.is_valid ?? true;

    if (!isTagsValid) {
      imageWithInvalidTagsIds.push(item.id);
    }

    try {
      const preparedItem = {
        ...item,
        ...getInitialImageCategoriesValues(item),
        hashtags: item?.hashtags?.data?.map((tag) => tag.id),
        publication_time: null,
      };

      const schema = validationSchema(
        preparedItem,
        noLinesHashtagId,
        mysteryCategoryId,
        true,
      );

      await schema.validate(preparedItem, { abortEarly: false });
    } catch (error) {
      if (error.inner) {
        errors[item.id] = error.inner.reduce((acc, err) => {
          acc[err.path] = err.message;
          return acc;
        }, {});
      }
    }
  });

  await Promise.all(validationPromises);

  let imageWithInvalidGeneralInfoIds = [];

  if (Object.keys(errors).length) {
    imageWithInvalidGeneralInfoIds = Object.keys(errors).map((item) => Number(item));
  }

  updateImageWithErrorsIds([ ...imageWithInvalidGeneralInfoIds ?? [], ...imageWithInvalidTagsIds ?? [] ]);

  imageWithInvalidTagsIds.forEach((id) => {
    errors[id] = { ...errors[id] ?? {}, tags: 'Invalid tags' };
  });

  return updateInitialImagesErrors(errors);
};

export const attachTagsValidParamToImages = (images, imageTagsData) => {
  return images.map((item) => {
    const isImageTagsValid = imageTagsData?.find((tag) => tag.image_id === item.id)?.is_valid
      ?? item.isTagsValid
      ?? true;

    return { ...item, isTagsValid: isImageTagsValid };
  });
};

export const mergeImageTags = (currentTagsData, newTagsData) => {
  const merged = [ ...currentTagsData ];
  const imageIdIndexMap = new Map();

  merged.forEach((obj, index) => {
    imageIdIndexMap.set(obj.image_id, index);
  });

  newTagsData.forEach((obj) => {
    if (imageIdIndexMap.has(obj.image_id)) {
      merged[imageIdIndexMap.get(obj.image_id)] = obj;
    } else {
      merged.push(obj);
    }
  });

  return merged;
};
